<template>
  <PageBreadcrumb
    :crumbs="[{
      label: 'Gestione Aste'
    }]"
  />

  <div class="flex justify-between mb-4">
    <div class="flex-grow">
      <SearchInput v-model="search" />
      <SearchSelect
        v-model="status"
        name="auction-status"
        :options="getAuctionStatusAllPluralLabels()"
      />
      <SearchSelect
        v-model="type"
        name="auction-type"
        :options="getAuctionTypeAllPluralLabels()"
      />
    </div>
    <div class="self-end">
      <ActionButton
        v-if="authStore.canManageAuctionOrCebis"
        @click="router.push('/aste/crea')"
      >
        <PlusCircleIcon class="w-5 h-5 inline-block" />
        Crea nuova Asta
      </ActionButton>
    </div>
  </div>

  <DataTable :data="data">
    <template #heading>
      <tr>
        <SortableColumn
          v-model="sortStatus"
          name="name"
        >
          Nome
        </SortableColumn>

        <SortableColumn
          v-model="sortStatus"
          name="auctionType"
        >
          Tipologia
        </SortableColumn>

        <SortableColumn
          v-model="sortStatus"
          name="auctionStatus"
        >
          Stato
        </SortableColumn>

        <StandardColumn>
          N. Lotti
        </StandardColumn>

        <th class="text-right p-2" />
      </tr>
    </template>
    <template #body>
      <tr
        v-for="auction in data"
        :key="'auction-table-tr-' + auction.uuid"
        class="odd:bg-gray-100"
      >
        <td class="p-2">
          {{ auction.name }}
        </td>
        <td class="p-2">
          {{ getAuctionTypeLabel(auction.auctionType) }}
        </td>
        <td class="p-2">
          {{ getAuctionStatusLabel(auction.auctionStatus) }}
          <span v-if="auction.auctionStatus === 'CLOSED'">
            <span
              v-if="auction.auctionCloseReason === 'COMPLETED'"
              class="text-xs"
            >(completata)</span>
            <span
              v-else-if="auction.auctionCloseReason === 'CANCELED'"
              class="text-xs"
            >(annullata)</span>

          </span>
        </td>
        <td class="p-2">
          {{ auction.lotsCount }}
        </td>
        <td class="p-2 text-right">
          <a
            v-if="auction.auctionStatus == 'DRAFT' && auction.lotsCount > 0 && authStore.canManageAuctionOrCebis"
            href="#"
            title="Pubblica Asta"
            @click="publish(auction)"
          >
            <BoltIcon class="h-5 w-5 inline-block mx-1" />
          </a>
          <a
            v-if="
              (auction.auctionStatus == 'PUBLISHED' || auction.auctionStatus == 'OPEN' || auction.auctionStatus == 'REDISTRIBUTING_1' || auction.auctionStatus == 'REDISTRIBUTING_2') &&
                authStore.canManageAuctionOrCebis"
            href="#"
            title="Annulla Asta"
            @click="cancel(auction)"
          >
            <BoltSlashIcon class="h-5 w-5 inline-block mx-1" />
          </a>

          <RouterLink
            v-if="auction.auctionStatus == 'DRAFT' && authStore.canManageAuctionOrCebis"
            title="Modifica Asta"
            :to="'/aste/' + auction.uuid"
          >
            <PencilSquareIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>

          <RouterLink
            v-if="auction.auctionStatus == 'DRAFT' || auction.auctionType == 'PUBLISHED'"
            title="Gestisci Lotti"
            :to="'/aste/' + auction.uuid + '/lotti'"
          >
            <CubeIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>

          <RouterLink
            v-if="auction.auctionStatus == 'CLOSED'"
            title="Visualizza Lotti"
            :to="'/aste/' + auction.uuid + '/visualizza-lotti'"
          >
            <ArchiveBoxIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>

          <RouterLink
            v-if="auction.auctionStatus == 'CLOSED' && auction.auctionType == 'REDISTRIBUTE'"
            title="Visualizza Lotti Redistribuzione"
            :to="'/aste/' + auction.uuid + '/visualizza-lotti-redistribuzione'"
          >
            <ArchiveBoxArrowDownIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>

          <RouterLink
            title="Gestisci Allegati"
            :to="'/aste/' + auction.uuid + '/allegati'"
          >
            <PaperClipIcon class="h-5 w-5 inline-block mx-1" />
          </RouterLink>
          <a
            v-if="authStore.canManageAuctionOrCebis && auction.auctionStatus == 'DRAFT'"
            href="#"
            title="Elimina Asta"
            @click.prevent="destroy(auction)"
          >
            <TrashIcon class="h-5 w-5 inline-block mx-1 " />
          </a>
        </td>
      </tr>
    </template>
  </DataTable>

  <DataTablePaginator
    :count="count"
    :count-all="countAll"
    :count-filtered="countFiltered"
    :current-page="currentPage"
    :has-next="hasNext"
    :has-prev="hasPrev"
    :next="next"
    :prev="prev"
    :total-pages="totalPages"
  />
</template>

<script setup>
import {
  ArchiveBoxArrowDownIcon,  ArchiveBoxIcon,
  BoltIcon,   BoltSlashIcon,
  CubeIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon} from "@heroicons/vue/24/outline";
import { ref, watch } from "vue";
import { useRouter } from 'vue-router'

import ActionButton from "@/components/ActionButton.vue";
import DataTable from "@/components/list/DataTable.vue";
import DataTablePaginator from "@/components/list/DataTablePaginator.vue";
import SearchInput from "@/components/list/SearchInput.vue";
import SearchSelect from "@/components/list/SearchSelect.vue";
import SortableColumn from "@/components/list/SortableColumn.vue";
import StandardColumn from "@/components/list/StandardColumn.vue";
import PageBreadcrumb from "@/components/PageBreadcrumb.vue";
import useDataTable from '@/composables/datatable.js'
import useEnums from '@/composables/enums.js'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const { getAuctionStatusLabel, getAuctionStatusAllPluralLabels, getAuctionTypeLabel, getAuctionTypeAllPluralLabels } = useEnums()

const search = ref('')
const status = ref()
const type = ref()

const sortStatus = ref(["name", -1]);

const { data, count, countFiltered, countAll, currentPage, totalPages, next, prev, hasPrev, hasNext, doFilter, action } = await useDataTable({
  endpoint: "/auction/list",
  sortStatus,
  search,
  searchProperties: ['name'],
  extraFilter: (item) => {
    return (status.value ? item.auctionStatus == status.value : true) &&
      (type.value ? item.auctionType == type.value : true)
  }
})

watch(status, () => {
  doFilter()
})

watch(type, () => {
  doFilter()
})

const publish = (auction) => {
  action('post', 'auction/publish/' + auction.uuid, 'Asta pubblicata con successo')
}

const cancel = (auction) => {
  action('post', 'auction/cancel/' + auction.uuid, 'Asta annullata con successo')
}

const destroy = (auction) => {
  action('delete', 'auction/' + auction.uuid, 'Asta eliminata con successo')
}
</script>
